import { IFlags, IFlagsmithFeature, useFlags } from 'flagsmith/react';

import { getEnvironmentVariables } from '~/environment';

export type FeatureName =
  | 'view-test-account-login'
  | 'view-all-reminders'
  | 'view-archived-classes'
  | 'view-weekly-insights'
  | 'view-classroom-moments'
  | 'view-student-data-screens'
  | 'view-email-password-signup-login-experience'
  | 'view-calendar-picker-on-review-page'
  | 'view-admin-setup'
  | 'outage-banner';
export type TraitName = string;

const { isMockMode } = getEnvironmentVariables();

export function useFeatureFlag(featureName: FeatureName): IFlagsmithFeature | undefined {
  const flags = useFlags<FeatureName, TraitName>([featureName]);
  return flags[featureName];
}

export function useTestAccountLoginFeature(): boolean {
  const testAccountLoginFlag = useFeatureFlag('view-test-account-login');
  return isMockMode || (testAccountLoginFlag?.enabled ?? false);
}

// Specific hook for View All Reminders button on Review page
export function useViewAllRemindersFeature(): boolean {
  const viewAllRemindersFlag = useFeatureFlag('view-all-reminders');
  return isMockMode || (viewAllRemindersFlag?.enabled ?? false);
}

export function useViewArchivedClassesFeature(): boolean {
  const viewArchivedClassesFlag = useFeatureFlag('view-archived-classes');
  return isMockMode || (viewArchivedClassesFlag?.enabled ?? false);
}

export function useViewWeeklyInsightsFeature(): boolean {
  const viewWeeklyInsightsFlag = useFeatureFlag('view-weekly-insights');
  return isMockMode || (viewWeeklyInsightsFlag?.enabled ?? false);
}

export function useViewClassroomMomentsFeature(): boolean {
  const viewClassroomMomentsFlag = useFeatureFlag('view-classroom-moments');
  return isMockMode || (viewClassroomMomentsFlag?.enabled ?? false);
}

export function useViewStudentDataScreensFeature(): boolean {
  const viewStudentDataScreensFlag = useFeatureFlag('view-student-data-screens');
  return isMockMode || (viewStudentDataScreensFlag?.enabled ?? false);
}

export function useEmailPasswordSignupLoginExperienceFeature(): boolean {
  const flag = useFeatureFlag('view-email-password-signup-login-experience');
  return isMockMode || (flag?.enabled ?? false);
}

export function useCalendarPickerOnReviewPageFeature(): boolean {
  const flag = useFeatureFlag('view-calendar-picker-on-review-page');
  return isMockMode || (flag?.enabled ?? false);
}

export function useAdminSetupFeature(): boolean {
  const flag = useFeatureFlag('view-admin-setup');
  return isMockMode || (flag?.enabled ?? false);
}

export function useOutageBannerMessage(): string | null {
  const flag = useFeatureFlag('outage-banner');
  if (!flag?.enabled) return null;
  const value = flag.value;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** Default flags to use if flagsmith is not available or not initalized.
 *
 * Note: These should match the Prod Flagsmith environment:
 * https://app.flagsmith.com/project/25247/environment/eFFh5LnYVPiX8QgmCL8NQw/features
 */
export const DEFAULT_FLAGS: IFlags<FeatureName> = {
  'view-test-account-login': {
    enabled: false,
  },
  'view-all-reminders': {
    enabled: false,
  },
  'view-archived-classes': {
    enabled: false,
  },
  'view-weekly-insights': {
    enabled: false,
  },
  'view-classroom-moments': {
    enabled: false,
  },
  'view-student-data-screens': {
    enabled: false,
  },
  'view-email-password-signup-login-experience': {
    enabled: false,
  },
  'view-calendar-picker-on-review-page': {
    enabled: false,
  },
  'view-admin-setup': {
    enabled: false,
  },
  'outage-banner': {
    enabled: false,
  },
};
